// src/components/common/SearchBar/SearchBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import styles from './SearchBar.module.css';

/**
 * SearchBar Component
 * 
 * @param {Object} props
 * @param {string} props.value - Current search value (controlled)
 * @param {function} props.onChange - Callback when search value changes
 * @param {function} props.onSearch - Callback when search is triggered (with debounce)
 * @param {function} props.onClear - Callback when clear button is clicked
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disable the search input
 * @param {boolean} props.autoSearch - Auto trigger search on change (with debounce, default: true)
 * @param {number} props.debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {string} props.size - Size variant: 'small' | 'medium' | 'large' (default: 'medium')
 * @param {string} props.className - Additional CSS class
 */
const SearchBar = ({
  value = '',
  onChange,
  onSearch,
  onClear,
  placeholder = 'Search...',
  disabled = false,
  autoSearch = true,
  debounceMs = 500,
  size = 'medium',
  className = ''
}) => {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle input change
  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    if (onChange) {
      onChange(newValue);
    }

    // Auto search with debounce
    if (autoSearch && onSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        onSearch(newValue);
      }, debounceMs);
    }
  };

  // Handle clear
  const handleClear = () => {
    setLocalValue('');
    
    if (onChange) {
      onChange('');
    }
    
    if (onClear) {
      onClear();
    }

    // If auto search is enabled, trigger search with empty value
    if (autoSearch && onSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      onSearch('');
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && onSearch && !autoSearch) {
      e.preventDefault();
      onSearch(localValue);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const wrapperClassName = `
    ${styles.searchBox} 
    ${styles[size]} 
    ${className}
  `.trim();

  return (
    <div className={wrapperClassName}>
      <Search className={styles.searchIcon} size={18} />
      
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        className={styles.searchInput}
      />

      {localValue && (
        <button
          onClick={handleClear}
          className={styles.clearSearchButton}
          type="button"
          title="Clear search"
          disabled={disabled}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default SearchBar;